import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CollectionImage } from '~/components/collections/CollectionImage';
import { Text } from '~/components/ui/Text';
import { IBait } from '~/rage';
import { baitItems, inventoryItems } from './SelectItem.model';
import {
	StyledContainer,
	StyledCount,
	StyledItemContainer,
	StyledWrapper,
} from './SelectItem.styled';

type BaitDisplayItem =
	| { itemId: string; count: number }
	| { id: number; icon: string };

const SelectItem = ({
	type,
	backpackAvailable,
	baitItems: baitItemsFromService,
	disabled,
	selectedItem,
	setSelectedItem,
}: {
	type: 'bait' | 'inventory';
	backpackAvailable?: boolean;
	baitItems?: IBait[];
	selectedItem: number;
	disabled?: boolean;
	setSelectedItem: (item: number) => void;
}) => {
	const isBaitFromService =
		type === 'bait' && baitItemsFromService !== undefined;

	const items = useMemo((): BaitDisplayItem[] | typeof inventoryItems => {
		if (type === 'inventory') return inventoryItems;
		if (isBaitFromService) {
			return [{ itemId: '', count: 0 }, ...baitItemsFromService];
		}
		return baitItems;
	}, [type, isBaitFromService, baitItemsFromService]);

	const [isOpen, setIsOpen] = useState(false);

	const selectedBaitCount = useMemo(() => {
		if (type !== 'bait' || !isBaitFromService || selectedItem === 0) return 0;
		const bait = baitItemsFromService[selectedItem - 1];
		return bait?.count ?? 0;
	}, [type, isBaitFromService, baitItemsFromService, selectedItem]);

	const height = useMemo(() => {
		if (!isOpen) {
			return 60;
		}

		if (type === 'bait') {
			if (selectedItem === 0) {
				return 40 * items.length + 10 * (items.length - 1) + 25;
			}

			return 40 * (items.length - 1) + 10 * (items.length - 2) + 25;
		}

		return 50 * items.length + 10 * (items.length - 1) + 15;
	}, [isOpen, items.length, type, selectedItem]);
	const ref = useRef<HTMLDivElement>(null);

	const sortedItems = useMemo(() => {
		if (type === 'bait') {
			if (selectedItem === 0) {
				return items;
			}
			const selected = items[selectedItem];
			const otherItems = (items as BaitDisplayItem[]).filter(
				(_item, index) => index !== selectedItem && index !== 0,
			);
			return selected ? [selected, ...otherItems] : items;
		}

		const result = [...inventoryItems];
		const selected = result.splice(selectedItem, 1)[0];
		return [selected, ...result];
	}, [selectedItem, type, items]);

	const isPlaceholder = useMemo(() => {
		return type === 'bait' && selectedItem === 0;
	}, [selectedItem, type]);

	const handleClick = useCallback(() => {
		if (!disabled) {
			setIsOpen((prev) => !prev);
		}
	}, [disabled]);

	useEffect(() => {
		function handleClickOutside(event: Event) {
			if (ref.current && !ref.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		}

		setTimeout(() => {
			document.addEventListener('click', handleClickOutside);
		}, 300);

		return () => {
			document.removeEventListener('click', handleClickOutside);
		};
	}, [ref]);

	return (
		<StyledWrapper>
			{type === 'bait' &&
				selectedItem !== 0 &&
				(selectedBaitCount > 0 || !isBaitFromService) && (
					<StyledCount style={{ opacity: isOpen ? 0 : 1 }}>
						<Text>{isBaitFromService ? selectedBaitCount : ''}</Text>
					</StyledCount>
				)}
			<StyledContainer
				ref={ref}
				style={{ height }}
				$isOpen={isOpen}
				$isInventory={type === 'inventory'}
				$disabled={disabled}
				onClick={handleClick}
				$isPlaceholder={isPlaceholder}
			>
				{sortedItems.map((item, index) => {
					const isBaitItem = 'itemId' in item;
					const isSelected = index === 0;
					const isDisabled =
						type === 'inventory' &&
						!isBaitItem &&
						(item as { id: number }).id === 2 &&
						!backpackAvailable;
					const transparent =
						type === 'bait' &&
						(isBaitItem
							? !(item as IBait).itemId
							: (item as { id: number }).id === 0);

					const originalIndex =
						type === 'inventory'
							? inventoryItems.findIndex(
									(orig) => orig.id === (item as { id: number }).id,
							  )
							: (items as BaitDisplayItem[]).findIndex((orig) =>
									isBaitItem && 'itemId' in orig
										? (orig as IBait).itemId === (item as IBait).itemId
										: !(orig as IBait).itemId && !(item as IBait).itemId,
							  );
					const safeOriginalIndex = originalIndex >= 0 ? originalIndex : index;

					const isInventoryInactive =
						type === 'inventory' && !isSelected && !isDisabled;

					return (
						<StyledItemContainer
							$isSelected={isSelected}
							$disabled={isDisabled}
							$isInventory={type === 'inventory'}
							$inventoryInactive={isInventoryInactive}
							key={
								isBaitItem
									? (item as IBait).itemId || 'placeholder'
									: (item as { id: number }).id
							}
							$transparent={transparent}
							onClick={(e) => {
								if (type === 'inventory' && isDisabled) {
									e.stopPropagation();
									setIsOpen(false);
									return;
								}
								if (!isSelected && !isDisabled) {
									e.stopPropagation();
									setSelectedItem(safeOriginalIndex);
									setIsOpen(false);
								}
							}}
						>
							{type === 'bait' && isBaitItem ? (
								(item as IBait).itemId ? (
									<CollectionImage
										collection="items"
										id={(item as IBait).itemId}
										size="md"
									/>
								) : (
									<img src={baitItems[0]?.icon} alt="" aria-hidden />
								)
							) : (
								<img src={(item as { icon: string }).icon} alt="item" />
							)}
						</StyledItemContainer>
					);
				})}
			</StyledContainer>
		</StyledWrapper>
	);
};

export { SelectItem };
